import { DropdownButton, Menu, MenuItem } from '@cmg/design-system';
import { useMemo, useRef, useState } from 'react';
import { Fragment } from 'react/jsx-runtime';

import { FilingType } from '../../../../../graphql';
import { filingTypeLabels } from '../../../../../types/domain/filing/constants';
import {
  OfferingDetails_PublishedOffering_FilingsFragment,
  OfferingDetails_PublishedOffering_TermsFragment,
} from '../../../../offering-side-panel/offering-details/graphql/__generated__';

type TermsDropdownButtonProps = {
  terms: OfferingDetails_PublishedOffering_TermsFragment;
  latestFilling: OfferingDetails_PublishedOffering_FilingsFragment;
  setLatestFiling: (filing: OfferingDetails_PublishedOffering_FilingsFragment) => void;
};

export function TermsDropdownButton({
  terms,
  latestFilling,
  setLatestFiling,
}: Readonly<TermsDropdownButtonProps>) {
  const btnRef = useRef<null | HTMLButtonElement>(null);
  const [isOpened, setIsOpened] = useState(false);

  const groupedFilings = useMemo(() => {
    return terms.filings.reduce<{
      [FilingType.Initial]?: OfferingDetails_PublishedOffering_FilingsFragment[];
      [FilingType.Revised]?: OfferingDetails_PublishedOffering_FilingsFragment[];
      [FilingType.Final]?: OfferingDetails_PublishedOffering_FilingsFragment[];
    }>((acc, filing) => {
      acc[filing.id] = filing;
      acc[filing.filingType] ??= [];
      acc[filing.filingType]?.push(filing);
      return acc;
    }, {});
  }, [terms]);

  const [initialFiling] = groupedFilings[FilingType.Initial] ?? [];
  const revisedFilings = groupedFilings[FilingType.Revised];
  const [finalFiling] = groupedFilings[FilingType.Final] ?? [];

  const filingsLabelsById = useMemo(() => {
    const labelsById: { [filingId: string]: string } = {};
    if (initialFiling) {
      labelsById[initialFiling.id] = filingTypeLabels[initialFiling.filingType];
    }
    revisedFilings?.reduce((acc, revisedFiling, index) => {
      acc[revisedFiling.id] = `${filingTypeLabels[revisedFiling.filingType]} ${index + 1}`;
      return acc;
    }, labelsById);

    if (finalFiling) {
      labelsById[finalFiling.id] = filingTypeLabels[finalFiling.filingType];
    }
    return labelsById;
  }, [initialFiling, revisedFilings, finalFiling]);

  return (
    <Fragment>
      <DropdownButton
        aria-label="Terms Dropdown Button"
        ref={btnRef}
        isExpanded={isOpened}
        onClick={() => setIsOpened(openState => !openState)}
        size="medium"
      >
        {filingsLabelsById[latestFilling.id]}
      </DropdownButton>

      <Menu open={isOpened} onClose={() => setIsOpened(false)} anchorEl={btnRef.current}>
        {initialFiling && (
          <MenuItem
            key={initialFiling.id}
            selected={latestFilling.id === initialFiling.id}
            onClick={() => {
              setLatestFiling(initialFiling);
              setIsOpened(false);
            }}
          >
            {filingsLabelsById[initialFiling.id]}
          </MenuItem>
        )}

        {revisedFilings &&
          revisedFilings.map(revisedFiling => {
            return (
              <MenuItem
                key={revisedFiling.id}
                selected={latestFilling.id === revisedFiling.id}
                onClick={() => {
                  setLatestFiling(revisedFiling);
                  setIsOpened(false);
                }}
              >
                {filingsLabelsById[revisedFiling.id]}
              </MenuItem>
            );
          })}
        {finalFiling && (
          <MenuItem
            key={finalFiling.id}
            selected={latestFilling.id === finalFiling.id}
            onClick={() => {
              setLatestFiling(finalFiling);
              setIsOpened(false);
            }}
          >
            {filingsLabelsById[finalFiling.id]}
          </MenuItem>
        )}
      </Menu>
    </Fragment>
  );
}
